// src/pages/calendar/CalendarSettingsPage.tsx
// Calendar settings: working hours, calendars & sharing, event templates,
// event type registry (admin), policies (admin). Tab is URL-driven (?tab=…).

import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { WorkingHoursSettings } from '@/features/calendar/settings/WorkingHoursSettings';
import { CalendarsSettings } from '@/features/calendar/settings/CalendarsSettings';
import { TemplatesSettings } from '@/features/calendar/settings/TemplatesSettings';
import { EventTypesSettings } from '@/features/calendar/settings/EventTypesSettings';
import { PoliciesSettings } from '@/features/calendar/settings/PoliciesSettings';

type Tab = 'working-hours' | 'calendars' | 'templates' | 'event-types' | 'policies';

const TABS: Tab[] = ['working-hours', 'calendars', 'templates', 'event-types', 'policies'];

export const CalendarSettingsPage = observer(() => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'working-hours';
  }, [searchParams]);

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        {/* The tab already names this page; the heading stays for screen readers. */}
        <h1 className="sr-only">{t('calendar.settingsTitle')}</h1>
        <Link to="/calendar" className="ml-auto text-sm text-primary hover:underline">
          {t('calendar.backToCalendar')}
        </Link>
      </div>

      <div role="tablist" aria-label={t('calendar.settingsTitle')} className="flex flex-wrap gap-1 border-b border-border">
        {TABS.map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={tab === v}
            onClick={() => setSearchParams({ tab: v })}
            className={`-mb-px border-b-2 px-3 py-2 text-sm ${
              tab === v
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(`calendar.settingsTab.${v}`)}
          </button>
        ))}
      </div>

      {tab === 'working-hours' && <WorkingHoursSettings />}
      {tab === 'calendars' && <CalendarsSettings />}
      {tab === 'templates' && <TemplatesSettings />}
      {tab === 'event-types' && <EventTypesSettings />}
      {tab === 'policies' && <PoliciesSettings />}
    </div>
  );
});
